import React, { FC, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller, useForm } from 'react-hook-form';
import Dialog from 'components/UI/Dialog';
import Text from 'components/UI/Text';
import Button from 'components/UI/Button';
import { theme } from 'components/UI';
import { TextField } from 'components/shared/TextField/TextField';
import Loading from 'components/UI/Loading/Loading';
import Grid from 'components/UI/Grid';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/hooks/hooks';
import { ActionMode, DialogOnCloseReason } from 'models/shared';
import api from 'Api/ApiMethods';
import styled from 'styled-components';
import { Select } from 'components/UI/Select/Select';
import { SchemaValidationEntityCode } from 'models/restTypes';

interface PlotDssDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
}

interface SchemaProperty {
  type?: string;
  enum?: unknown[];
  minimum?: number;
  maximum?: number;
  default?: unknown;
  title?: string;
  description?: string;
}

interface RawParamOption {
  enum?: unknown[];
  options?: { enum_titles?: string[] };
}

interface SelectOption {
  label: string;
  value: unknown;
}

type DssFormValues = Record<string, unknown>;

const StyledDialogContent = styled(DialogContent)`
  padding: 0.5rem 1.5rem;
  min-height: 300px;
`;

const StyledAccordion = styled(Accordion)`
  box-shadow: none;
  border-bottom: 1px solid var(--surface-border);
  background-color: transparent;
  &::before {
    display: none;
  }
  &.Mui-expanded {
    margin: 0;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  min-height: 40px;
  padding: 0 4px;
  &.Mui-expanded {
    min-height: 40px;
  }
  .MuiAccordionSummary-content {
    margin: 8px 0;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 8px 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FieldLabel = styled(Typography)`
  min-width: 160px;
  font-size: 0.85rem;
  color: var(--text-color);
`;

const FIELD_GROUPS: { key: string; fields: string[] }[] = [
  { key: 'sensor', fields: ['enableRAW', 'soilSensor', 'soilSensorData'] },
  {
    key: 'irrigation',
    fields: [
      'soilMin',
      'soilMax',
      'swhcRange',
      'irrigationMin',
      'irrigationMax',
    ],
  },
  {
    key: 'plan',
    fields: [
      'frequency',
      'disableF',
      'correctionFactor',
      'disableCF',
      'planDuration',
    ],
  },
  { key: 'rain', fields: ['enableRainCf', 'minTempThrsh', 'maxTempThrsh'] },
];

// const additionalOptions = [
//   { id: false, name: 'auto' },
//   { id: true, name: 'manual' }
// ]

// Fields that represent auto/manual mode — rendered as a dropdown instead of a switch.
const AUTO_MANUAL_FIELDS = ['disableF', 'disableCF'];
const AUTO_MANUAL_OPTIONS: SelectOption[] = [
  { label: 'Auto', value: false },
  { label: 'Manual', value: true },
];

const GROUP_LABELS: Record<string, string> = {
  sensor: 'Sensor',
  irrigation: 'Irrigation',
  plan: 'Plan',
  rain: 'Adjust to Rain',
};

const PlotDssDialog: FC<PlotDssDialogProps> = observer(
  ({ visible = false, onHide, title }) => {
    const { t } = useTranslation('plot');
    const { resellersStore, rootStore } = useStores();
    const plotId = resellersStore.selectedRowsPlots[0]?.id;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [schemaProps, setSchemaProps] = useState<
      Record<string, SchemaProperty>
    >({});

    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    const [selectOptions, setSelectOptions] = useState<
      Record<string, SelectOption[]>
    >({});

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<DssFormValues>({ mode: 'onChange' });

    // Fetches the DSS validation schema and current plot params in parallel,
    // then populates form state with defaults merged with saved values.
    const loadDssSettings = useCallback(
      async (controller: AbortController) => {
        try {
          const [validation, paramsData] = await Promise.all([
            api.getSchemaValidation(
              SchemaValidationEntityCode.RECOMMENDATION_PARAMS,
              controller,
            ),
            api.getPlotRecommendationParams(plotId, controller),
          ]);

          const properties: Record<string, SchemaProperty> =
            validation?.schema?.properties ?? {};

          const required: string[] = validation?.schema?.required ?? [];

          setSchemaProps(properties);
          setRequiredFields(required);

          const rawOpts = (paramsData?.paramsOptions ?? {}) as Record<
            string,
            RawParamOption
          >;

          const formatted = Object.fromEntries(
            Object.entries(rawOpts).map(([key, opt]) => {
              const enums = opt?.enum ?? [];
              const titles = opt?.options?.enum_titles ?? [];
              return [
                key,
                enums.map((val, i) => ({
                  value: val,
                  label: titles[i] ?? String(val),
                })),
              ];
            }),
          );

          setSelectOptions(formatted);

          const currentParams = (paramsData?.params ?? {}) as Record<
            string,
            unknown
          >;

          const defaults = Object.fromEntries(
            Object.entries(properties).map(([key, prop]) => [
              key,
              currentParams[key] ??
              prop.default ??
              (prop.type === 'boolean' ? false : ''),
            ]),
          );

          console.log('Defaults', defaults);
          console.log('properties', properties);
          reset({ ...defaults, comment: '' });
        } catch {
          // load failed - loading state will be cleared in finally
        } finally {
          setLoading(false);
        }
      },
      [plotId, reset],
    );

    // Triggers data loading when the dialog opens or the plot changes.
    // The AbortController cancels in-flight requests on cleanup.
    useEffect(() => {
      const controller = new AbortController();

      if (plotId) {
        loadDssSettings(controller);
      }

      return () => {
        controller.abort();
      };
    }, [plotId, loadDssSettings]);

    // Prevents closing the dialog by clicking the backdrop; allows ESC and button close.
    const handleClose = useCallback(
      (_event: unknown, reason: DialogOnCloseReason) => {
        if (reason !== DialogOnCloseReason.BackDropClick) onHide();
      },
      [onHide],
    );

    const onError = useCallback((formErrors: Record<string, unknown>) => {
      const firstKey = Object.keys(formErrors)[0];
      if (firstKey) {
        document.getElementById(firstKey)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, []);

    // Posts the updated DSS params and comment to the API, then closes the dialog on success.
    const onSubmit = useCallback(
      async (values: DssFormValues) => {
        const { comment, ...params } = values;
        try {
          setSaving(true);
          const res = await api.postRecommendationParams(plotId, {
            comment: comment as string,
            params,
          });

          console.log('Submitted values', values);
          console.log('params', params)
          setSaving(false);
          if (res) onHide();

        } catch (error) {
          console.error('Error submitting DSS params', error);
          rootStore?.snackBarStore.showError('Error submitting DSS params');
          setSaving(false);
        }
      },
      [plotId, onHide, rootStore?.snackBarStore],
    );

    // Builds react-hook-form validation rules for a given field key based on
    // the server schema (required, numeric min/max).
    const getFieldRules = useCallback(
      (key: string) => {
        const prop = schemaProps[key];
        if (!prop) return {};

        return {
          required: requiredFields.includes(key)
            ? `${prop.title || key} is required`
            : false,
          ...((prop.type === 'number' || prop.type === 'integer') && {
            valueAsNumber: true,
            min:
              prop.minimum !== undefined
                ? { value: prop.minimum, message: `Min: ${prop.minimum}` }
                : undefined,
            max:
              prop.maximum !== undefined
                ? { value: prop.maximum, message: `Max: ${prop.maximum}` }
                : undefined,
          }),
        };
      },
      [schemaProps, requiredFields],
    );

    // Renders the appropriate input for a schema field:
    // disableF/disableCF → Auto/Manual dropdown, other booleans → Switch,
    // enum/select options → Dropdown, number/integer → TextField.
    const renderField = useCallback(
      (key: string) => {
        const prop = schemaProps[key];
        if (!prop) return null;

        const label = prop.title || key;
        const description = prop.description ?? '';
        const error = errors[key];

        if (prop.type === 'boolean') {
          if (AUTO_MANUAL_FIELDS.includes(key)) {
            return (
              <FieldRow key={key} id={key} style={{ width: '150px' }}>
                {/* <FieldLabel>{label}</FieldLabel> */}
                <Controller
                  name={key}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      options={AUTO_MANUAL_OPTIONS}
                      optionLabel="label"
                      optionValue="value"
                      value={value}
                      onChange={(e) => onChange(e.value)}
                      style={{ flex: 0 }}
                    />
                  )}
                />
              </FieldRow>
            );
          }

          return (
            <FieldRow key={key} id={key}>
              <Controller
                name={key}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormControlLabel
                    label={
                      <Tooltip title={description} placement="top">
                        <FieldLabel>{label}</FieldLabel>
                      </Tooltip>
                    }
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                  />
                )}
              />
            </FieldRow>
          );
        }

        const enumOptions: SelectOption[] =
          selectOptions[key] ??
          prop.enum?.map((v: unknown) => ({ value: v, label: String(v) }));

        if (enumOptions) {
          return (
            <FieldRow key={key} id={key}>
              <Tooltip title={description} placement="top">
                <FieldLabel>{label}</FieldLabel>
              </Tooltip>
              <Controller
                name={key}
                control={control}
                rules={getFieldRules(key)}
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={enumOptions}
                    optionLabel="label"
                    optionValue="value"
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    error={!!error}
                    style={{ flex: 1 }}
                  />
                )}
              />
              {error && (
                <Typography variant="caption" color="error">
                  {error.message as string}
                </Typography>
              )}
            </FieldRow>
          );
        }

        return (
          <FieldRow key={key} id={key}>
            <Tooltip title={description} placement="top">
              <FieldLabel>{label}</FieldLabel>
            </Tooltip>
            <Controller
              name={key}
              control={control}
              rules={getFieldRules(key)}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) =>
                    field.onChange((e.target as HTMLInputElement).valueAsNumber)
                  }
                  value={field.value as string | number}
                  type="number"
                  size="small"
                  error={!!error}
                  helperText={error?.message as string}
                  style={{ flex: 1 }}
                />
              )}
            />
          </FieldRow>
        );
      },
      [schemaProps, selectOptions, errors, control, getFieldRules],
    );

    return (
      <Dialog
        open={visible}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <StyledDialogContent>
          {loading ? (
            <Grid
              direction="row"
              align="center"
              justify="center"
              style={{ height: '300px' }}
            >
              <Loading />
            </Grid>
          ) : (
            <>
              {schemaProps.recommendation && (
                <Controller
                  name="recommendation"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                      label={
                        <Text size="sm">
                          {schemaProps.recommendation?.title ||
                            'Recommendation'}
                        </Text>
                      }
                      control={
                        <Switch
                          checked={value === 'On'}
                          onChange={(e) =>
                            onChange(e.target.checked ? 'On' : 'Off')
                          }
                          color="primary"
                        />
                      }
                    />
                  )}
                />
              )}

              {/* {FIELD_GROUPS.map(({ key, fields }) => {
                const visibleFields = fields.filter((f) => !!schemaProps[f]);
                if (!visibleFields.length) return null;

                return (
                  <StyledAccordion key={key} defaultExpanded>
                    <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight={600} fontSize="0.9rem">
                        {GROUP_LABELS[key]}
                      </Typography>
                    </StyledAccordionSummary>
                    <StyledAccordionDetails>
                      {visibleFields.map(renderField)}
                    </StyledAccordionDetails>
                  </StyledAccordion>
                );
              })} */}

              <StyledAccordion key={FIELD_GROUPS[0].key} defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {GROUP_LABELS[FIELD_GROUPS[0].key]}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {renderField('enableRAW')}
                  {renderField('soilSensor')}
                  {renderField('soilSensorData')}
                </StyledAccordionDetails>
              </StyledAccordion>

              <StyledAccordion key={FIELD_GROUPS[1].key} defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {GROUP_LABELS[FIELD_GROUPS[1].key]}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {renderField('soilMin')}
                  {renderField('soilMax')}
                  {renderField('swhcRange')}
                  {renderField('irrigationMin')}
                  {renderField('irrigationMax')}
                </StyledAccordionDetails>
              </StyledAccordion>

              <StyledAccordion key={FIELD_GROUPS[2].key} defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {GROUP_LABELS[FIELD_GROUPS[2].key]}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                    {renderField('frequency')}
                    {renderField('disableF')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                    {renderField('correctionFactor')}
                    {renderField('disableCF')}
                  </div>
                  {renderField('planDuration')}
                </StyledAccordionDetails>
              </StyledAccordion>

              <StyledAccordion key={FIELD_GROUPS[3].key} defaultExpanded>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {GROUP_LABELS[FIELD_GROUPS[3].key]}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  {renderField('enableRainCf')}
                  {renderField('minTempThrsh')}
                  {renderField('maxTempThrsh')}
                </StyledAccordionDetails>
              </StyledAccordion>

              <FieldRow id="comment" style={{ marginTop: '12px' }}>
                <Controller
                  name="comment"
                  control={control}
                  rules={{ required: 'Comment is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value as string}
                      label={t('comment')}
                      multiline
                      rows={2}
                      fullWidth
                      error={!!errors.comment}
                      helperText={errors.comment?.message as string}
                    />
                  )}
                />
              </FieldRow>
            </>
          )}
        </StyledDialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={handleSubmit(onSubmit, onError)}
            loading={saving}
            disabled={loading || saving}
          >
            <Text textcolor={theme.color.white} size="sm">
              {t('edit')}
            </Text>
          </Button>
          <Button onClick={onHide}>
            <Text textcolor={theme.color.white} size="sm">
              {t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default PlotDssDialog;
